import React from "react";
import * as d3 from "d3";
import { ScaleLinear } from "d3";

import { Bounds2D } from "../LineChartUtils";
import { TickInterval } from "./LineChart";
import { useTheme } from "@mui/material";

interface XAxisProps {
  bounds: Bounds2D;
  fontSize: number;
  scale: ScaleLinear<number, number>;
  tickFormatter?(value: number, index: number, domain: Array<number>): string;
  tickInterval?: TickInterval;
}

type TextAnchor = "start" | "middle" | "end" | "inherit" | undefined;

export default function XAxis(props: XAxisProps): React.JSX.Element {
  const theme = useTheme();
  const { height, width, x, y } = props.bounds;
  const tickLength = props.fontSize / 2;

  // Ticks are currently spaced for about 12 characters where the average character width is 0.67x the height.
  // This could probably be refined later.
  const tickCountMax = Math.floor(Math.min(width / (12 * props.fontSize * 0.67)));
  const domain = props.scale.domain();
  let ticks: Array<number> = [];
  if (tickCountMax === 1) {
    // Only show the maximum domain value.
    ticks = [domain[1]];
  } else if (tickCountMax === 2 || props.tickInterval === "firstLast") {
    // Only show the minimum and maximum domain value.
    ticks = domain;
  } else if (tickCountMax > 2 && props.tickInterval === "interval") {
    // Create a number of ticks between the minimum and maximum domain value.
    if (domain[1] < tickCountMax) {
      ticks = d3.quantize(d3.interpolate(domain[0], domain[1]), tickCountMax);
    } else {
      ticks = d3.quantize(d3.interpolateRound(domain[0], domain[1]), tickCountMax);
    }
  }

  return (
    <g
      name="x-axis"
      transform={`translate(${x}, ${y})`}
      height={height}
      width={width}
    >
      <path
        d={["M", 0, 0, "L", width, 0].join(" ")}
        fill="none"
        stroke={theme.palette.text.secondary}
        strokeWidth={1}
      />

      <g name="ticks">
        {ticks.map((value, index) => {
          const xPos = props.scale(value);

          let textAnchor: TextAnchor = "middle";
          if (index === 0 && ticks.length > 1) {
            textAnchor = "start";
          } else if (index === ticks.length-1) {
            textAnchor = "end"
          }

          let tickText = `${value}`;
          if (props.tickFormatter) {
            tickText = props.tickFormatter(value, index, props.scale.domain());
          }

          return (
            <g
              key={`tick-${index}`}
              transform={`translate(${xPos}, 0)`}
            >
              <line y2={tickLength} stroke={theme.palette.text.secondary} strokeWidth={1} />
              <text
                style={{
                  fill: theme.palette.text.secondary,
                  fontSize: `${props.fontSize}px`,
                  textAnchor: textAnchor,
                  transform: `translateY(${props.fontSize + tickLength}px)`
                }}
              >
                {tickText}
              </text>
            </g>
          );
        })}
      </g>
    </g>
  );
}

/*

TICKS: 
- render start and end

When multiple lines are aligned at the start: 
- to stack, just use relative time (normalize all date ranges to start at 0)
- in that case, the legend should be relative as well, like always 0 on the left, and like "3 months" or whatever on the right

*/